import { useEffect, useRef, useState, type FormEvent } from 'react';
import { Link } from '@tanstack/react-router';
import { BookLock, Check, Search, CircleAlert } from 'lucide-react';
import { useReserviert } from '@/hooks/useReserviert';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { isbnAnzeige } from '@/lib/isbn';
import { ApiError } from '@/lib/api';
import type { ReserviertTreffer } from '@/types/reserviert';
import { FOKUS_RING } from '@/lib/fokus';

// ---------------------------------------------------------------------
// „Ist dieses Exemplar für jemanden reserviert?" (#59, A27)
//
// DER FALL AM TRESEN
//   BONUS zeigt ein Exemplar als vorhanden an. Physisch liegt es
//   reserviert im Regal für eine offene Kundenbestellung — als
//   Teillieferung, oder weil noch auf das zweite Exemplar gewartet wird.
//   Der Bestand ist ein Phantom: er existiert in der Warenwirtschaft und
//   ist nicht verfügbar. Wer das nicht weiß, verkauft das Exemplar, das
//   einer anderen Kundin gehört.
//
// EIN FELD, EINE ANTWORT
//   Wer hier steht, will nicht eine Trefferliste lesen und selbst
//   schließen — er will einen Satz. Der Satz steht oben und ist groß;
//   die Einzelheiten stehen darunter, für den, der sie braucht.
//
//   Der Satz kommt vom SERVER (`satz` in der Antwort) und wird hier
//   nicht zweitgebaut. Sonst hätten Backend und Oberfläche zwei Meinungen
//   darüber, was „2 Exemplare" heißt (Designprinzip 4).
//
//   SEIT A37.2 (#105) NENNT DER SATZ DEN GESUCHTEN BEGRIFF. Vorher stand
//   dort „nichts offen zu diesem Titel" — zu welchem? Der Begriff stand
//   nur im Feld, und der ist schnell weg: geleert, überschrieben, oder
//   das ×-Symbol des Suchfeldes gedrückt. Dann bezog sich die Antwort
//   auf nichts Sichtbares mehr. Auch das erledigt der Server; hier wird
//   nichts angeklebt. `break-words` hält einen sehr langen Begriff
//   innerhalb der Kastenbreite.
//
// WARUM AUF ENTER UND NICHT BEI JEDEM ANSCHLAG
//   Die Antwort ist ein SATZ. Ein Satz, der sich bei jedem Buchstaben neu
//   schreibt, lässt sich nicht lesen — und „Nein, nichts offen" wäre
//   nach drei Zeichen fast immer die Antwort, die man dann für endgültig
//   hält. Dazu käme eine Anfrage je Anschlag.
//
// WARUM EINE EIGENE SEITE UND KEIN DIALOG
//   Ein Dialog nimmt das Dashboard in Geiselhaft und geht bei jedem
//   Wegtippen zu. Am Auspack- und Tresentisch ist das hier eine STEHENDE
//   Aufgabe: Das Tablet darf den ganzen Vormittag auf dieser Seite
//   liegenbleiben, und der Weg zurück ist ein Griff. Ein Eintrag in der
//   Seitenleiste macht daraus GENAU EINEN Handgriff vom Dashboard aus.
//
// DIE UNTERSCHEIDUNG TRÄGT NICHT DIE FARBE (A24)
//   Coral (#993C1D) und Teal (#1D6B6B) haben zueinander 1,12:1 — sie
//   sind fast exakt gleich hell. Wer „reserviert" und „frei" allein an
//   der Randfarbe unterscheiden wollte, sagte auf einem Tablet in der
//   Sonne und für jeden mit Rotsehschwäche gar nichts.
//
//   Deshalb tragen die beiden Fälle verschiedene WORTE („Ja" / „Nein"),
//   verschiedene SYMBOLE (Schloss / Haken) und verschiedene RAHMENSTÄRKE
//   (2 px / 1 px). Die Farbe kommt obendrauf. Gemessen gegen die
//   Hauptfläche Cream (#FDFBF7): Coral 6,73:1, Ink 13,70:1 — beide über
//   den 4,5:1 für Text.
// ---------------------------------------------------------------------

// SEIT 02.11.2026 EIN BAUTEIL FÜR ZWEI ORTE: die Seite „Reserviert?" und
// das Blatt, das sich über jede Seite legt (ReserviertBlatt.tsx). Beide
// führen den gefragten Begriff in der Adresse — die Seite als `q`, das
// Blatt als `reserviert` —, deshalb kommt er von außen herein und geht
// über `onFragen` wieder hinaus.

export function ReserviertInhalt({
  begriff,
  onFragen
}: {
  /** Was zuletzt GEFRAGT wurde (aus der Adresse). */
  begriff: string;
  onFragen: (begriff: string) => void;
}) {
  // Was im Feld steht, und was zuletzt gefragt wurde. Zwei Zustände,
  // weil die Antwort zum gefragten Begriff gehört, nicht zum getippten.
  const [feld, setFeld] = useState(begriff);
  // Die gestellte Frage führt das Bauteil AUCH selbst: Sie gilt sofort,
  // nicht erst, wenn die Adresse nachgezogen hat — und bleibt, falls ein
  // Aufrufer die Adresse gar nicht ändert.
  const [gefragt, setGefragt] = useState(begriff);
  const eingabe = useRef<HTMLInputElement>(null);
  const q = useReserviert(gefragt);

  useEffect(() => {
    setFeld(begriff);
    setGefragt(begriff);
  }, [begriff]);

  // Am Tresen wird getippt, nicht geklickt: der Fokus liegt beim Öffnen
  // im Feld.
  useEffect(() => {
    eingabe.current?.focus();
  }, []);

  function fragen(e: FormEvent): void {
    e.preventDefault();
    setGefragt(feld.trim());
    onFragen(feld.trim());
  }

  const zuKurz = feld.trim() !== '' && feld.trim().length < 3;
  const antwort = q.data;

  return (
    <div className="space-y-4">
      <form onSubmit={fragen} className="flex flex-wrap items-end gap-2">
        <label className="flex min-w-0 flex-1 flex-col gap-1 text-sm text-ink-soft">
          Titel oder ISBN
          <input
            ref={eingabe}
            type="search"
            value={feld}
            onChange={(e) => setFeld(e.target.value)}
            placeholder="z. B. Zauberberg oder 9783423282789"
            aria-describedby={zuKurz ? 'reserviert-zu-kurz' : undefined}
            className={`min-h-[44px] w-full rounded-md border border-line bg-cream px-3 text-base text-ink ${FOKUS_RING}`}
          />
        </label>
        <Button variant="primary" type="submit" disabled={zuKurz}>
          <Search aria-hidden className="mr-1.5 h-4 w-4" />
          Nachsehen
        </Button>
      </form>

      {zuKurz && (
        <p id="reserviert-zu-kurz" className="text-sm text-ink-soft">
          Bitte mindestens drei Zeichen — zwei treffen fast jeden Titel.
        </p>
      )}

      {q.isFetching && (
        <p className="flex items-center gap-2 text-sm text-ink-soft">
          <Spinner /> Wird nachgesehen …
        </p>
      )}

      {q.isError && !q.isFetching && (
        <p className="inline-flex items-center gap-2 rounded-md border border-coral px-3 py-2 text-sm text-coral">
          <CircleAlert aria-hidden className="h-4 w-4 shrink-0" />
          {q.error instanceof ApiError ? q.error.message : 'Die Abfrage schlug fehl.'}
        </p>
      )}

      {antwort !== undefined && !q.isFetching && (
        <section className="space-y-3" aria-live="polite">
          <Antwortsatz reserviert={antwort.reserviert} satz={antwort.satz} />

          {antwort.treffer.length > 0 && (
            <ul className="space-y-2">
              {antwort.treffer.map((t) => (
                <li key={`${t.bestellung_id}-${t.titel}-${t.isbn ?? ''}`}>
                  <TrefferZeile treffer={t} />
                </li>
              ))}
            </ul>
          )}
        </section>
      )}
    </div>
  );
}

/**
 * Der Satz oben.
 *
 * Wort, Symbol und Rahmenstärke unterscheiden die beiden Fälle; die
 * Farbe kommt obendrauf. Siehe der Kopf dieser Datei — Coral und Teal
 * stehen zueinander bei 1,12:1.
 */
export function Antwortsatz({ reserviert, satz }: { reserviert: boolean; satz: string }) {
  if (reserviert) {
    return (
      <p
        data-antwort="ja"
        className="flex items-start gap-2 break-words rounded-md border-2 border-coral bg-cream px-3 py-2 font-serif text-lg font-medium text-coral"
      >
        <BookLock aria-hidden className="mt-0.5 h-5 w-5 shrink-0" />
        {satz}
      </p>
    );
  }
  return (
    <p
      data-antwort="nein"
      className="flex items-start gap-2 break-words rounded-md border border-line bg-cream px-3 py-2 font-serif text-lg text-ink"
    >
      <Check aria-hidden className="mt-0.5 h-5 w-5 shrink-0 text-akzent" />
      {satz}
    </p>
  );
}

/**
 * Eine Zeile der Einzelheiten — und der Weg in die Bestellung.
 *
 * Wer sieht, dass reserviert ist, will als Nächstes wissen, für wen und
 * seit wann. Ein Tipp führt in die Bestellung; das ist derselbe Griff
 * wie überall sonst in der Oberfläche.
 */
function TrefferZeile({ treffer: t }: { treffer: ReserviertTreffer }) {
  const teil = t.eingetroffen > 0 && t.eingetroffen < t.menge;
  return (
    <Link
      to="/bestellungen/$bestellnr"
      params={{ bestellnr: t.bestellnr }}
      className="flex min-h-[44px] flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-line bg-cream-warm px-3 py-2 text-sm hover:border-akzent"
    >
      {/* Mindestbreite: Im schmalen Blatt (Handy) rutschen die Zahlen
          rechts darunter, statt Titel und Namen auf „Papyrus und …"
          zusammenzudrücken (gemessen 02.11.2026 bei 390 px). */}
      <span className="min-w-[12rem] flex-1">
        <span className="block truncate font-medium text-ink">{t.titel}</span>
        <span className="block truncate text-xs text-ink-soft">
          {t.kunde} · {t.bestellnr}
          {t.isbn !== null && ` · ${isbnAnzeige(t.isbn)}`}
        </span>
      </span>
      <span className="flex shrink-0 flex-wrap items-center gap-2 text-xs text-ink-soft">
        {/* Die Zahlen tragen ihr eigenes Wort. „2/3" allein sagt am
            Regal nicht, welche der beiden Zahlen die eingetroffene ist. */}
        <span className="tabular-nums">
          {t.eingetroffen} von {t.menge} da
        </span>
        {/* `text-ink` und nicht `text-gold-dark` wie an den sechs
            älteren Stellen: Gold-dark auf Gold-soft ist 3,56:1 und
            reißt damit die 4,5:1 aus A24. Mit Ink sind es 11,91:1.
            Die Abweichung im Aussehen ist Absicht und im Bericht
            vermerkt — die Untergrenze schlägt die Einheitlichkeit. */}
        {teil && <span className="rounded bg-gold-soft px-1.5 py-0.5 text-ink">Teillieferung</span>}
        <span className="rounded border border-line px-1.5 py-0.5">{t.zustand.label}</span>
        <span className="tabular-nums">
          {t.tage_offen === 0 ? 'heute' : `seit ${t.tage_offen} Tag${t.tage_offen === 1 ? '' : 'en'}`}
        </span>
      </span>
    </Link>
  );
}
